import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, ListOrdered, Loader2, Zap } from "lucide-react";
import {
  DEFAULT_EXCHANGE_TIER_CONFIGS,
  AutotradeTrigger,
  AutotradeTriggerCategory,
  TriggerSource,
  TriggerPriceAlertType,
  CreateNewsAlertTriggerPayload,
  CreatePriceAlertTriggerPayload,
  UpdateTriggerPayload,
  TriggerExchangeInput,
} from "@crypto-trading/common/browser";
import { toast } from "sonner";
import { TabSelector } from "./TabSelector";
import { PriceMoveRoiHint } from "./PriceMoveRoiHint";
import { SegmentedControl } from "./SegmentedControl";
import {
  useCreateTrigger,
  useUpdateTrigger,
  useDeleteTrigger,
} from "@/hooks/useAutotradeTriggers";
import { useUserExchanges } from "@/hooks/useExchanges";

// ─── Exchange helpers ─────────────────────────────────────────────────────────

const EXCHANGE_NAME_MAP: Record<string, string> = {
  binance: "Binance",
  coinbase: "Coinbase",
  coinbaseinternational: "Coinbase International",
  bitvavo: "Bitvavo",
  okx: "OKX",
  upbit: "Upbit",
  bithumb: "Bithumb",
  bybit: "Bybit",
  robinhood: "Robinhood",
  hyperliquid: "Hyperliquid",
  aevo: "Aevo",
  gateio: "Gate.io",
  kucoin: "KuCoin",
  aster: "AsterDEX",
};

const formatExchangeName = (code: string) =>
  EXCHANGE_NAME_MAP[code] || code.charAt(0).toUpperCase() + code.slice(1);

const formatTypeLabel = (type: string) =>
  type
    .split(/[_-]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

interface ExchangeOption {
  exchangeCode: string;
  exchangeType: string;
  label: string;
}

const ALL_EXCHANGE_OPTIONS: ExchangeOption[] =
  DEFAULT_EXCHANGE_TIER_CONFIGS.flatMap((cfg) => {
    const types = new Set([
      ...Object.keys(cfg.listingTiers),
      ...Object.keys(cfg.delistingTiers),
    ]);
    return Array.from(types).map((type) => {
      const label = `${formatExchangeName(cfg.exchange)} - ${formatTypeLabel(type)}`;
      return {
        exchangeCode: cfg.exchange,
        exchangeType: type,
        label,
      };
    });
  });

const PRICE_TIMEFRAME_OPTIONS = ["1m", "5m", "15m", "30m", "1h", "4h"];

/**
 * Same ids as `PriceAlertConfig.exchange` / price webhook — detector only supports these.
 * Rows use tier codes: binance+futures, gateio+futures.
 */
const PRICE_AUTOTRADE_VENUES: Array<{
  venueId: string;
  label: string;
  exchangeCode: string;
  exchangeType: string;
}> = [
  {
    venueId: "binance_futures",
    label: "Binance Futures",
    exchangeCode: "binance",
    exchangeType: "futures",
  },
  {
    venueId: "gate_futures",
    label: "Gate Futures",
    exchangeCode: "gateio",
    exchangeType: "futures",
  },
];

type TriggerEventKind = "news" | "price";

// ─── Form state ────────────────────────────────────────────────────────────────

interface TriggerFormState {
  eventKind: TriggerEventKind;
  category: AutotradeTriggerCategory;
  priceAlertType: TriggerPriceAlertType;
  priceChangeThreshold: string;
  priceTimeframe: string;
  tradingPair: string;
  positionType: "LONG" | "SHORT";
  amount: number;
  leverage: number;
  exactLeverage: boolean;
  takeProfitPercent: string;
  stopLossPercent: string;
  slippagePercent: string;
  positionTimeout: string;
  /** Power mode (listing/delisting only). */
  openOnAllExchanges: boolean;
}

const defaultFormState: TriggerFormState = {
  eventKind: "news",
  category: AutotradeTriggerCategory.LISTING,
  priceAlertType: TriggerPriceAlertType.SPIKE,
  priceChangeThreshold: "5",
  priceTimeframe: "5m",
  tradingPair: "",
  positionType: "LONG",
  amount: 0,
  leverage: 5,
  exactLeverage: false,
  takeProfitPercent: "",
  stopLossPercent: "",
  slippagePercent: "",
  positionTimeout: "",
  openOnAllExchanges: false,
};

function formStateFromTrigger(trigger: AutotradeTrigger): TriggerFormState {
  const base: Omit<
    TriggerFormState,
    | "eventKind"
    | "category"
    | "priceAlertType"
    | "priceChangeThreshold"
    | "priceTimeframe"
    | "tradingPair"
  > = {
    positionType: trigger.hotkey.positionType,
    amount: trigger.hotkey.amount,
    leverage: trigger.hotkey.leverage,
    exactLeverage: trigger.hotkey.exactLeverage ?? false,
    takeProfitPercent:
      trigger.hotkey.takeProfitPercent == null
        ? ""
        : String(trigger.hotkey.takeProfitPercent),
    stopLossPercent:
      trigger.hotkey.stopLossPercent == null
        ? ""
        : String(trigger.hotkey.stopLossPercent),
    slippagePercent:
      trigger.hotkey.slippagePercent == null
        ? ""
        : String(trigger.hotkey.slippagePercent),
    positionTimeout:
      trigger.hotkey.positionTimeout == null
        ? ""
        : String(trigger.hotkey.positionTimeout),
    openOnAllExchanges: trigger.openOnAllExchanges ?? false,
  };

  if (trigger.triggerSource === TriggerSource.PRICE_ALERT) {
    return {
      ...base,
      eventKind: "price",
      category: AutotradeTriggerCategory.LISTING,
      priceAlertType: trigger.priceAlertType ?? TriggerPriceAlertType.SPIKE,
      priceChangeThreshold:
        trigger.priceChangeThreshold != null
          ? String(trigger.priceChangeThreshold)
          : "5",
      priceTimeframe: trigger.priceTimeframe ?? "5m",
      tradingPair: trigger.tradingPair ?? "",
    };
  }

  return {
    ...base,
    eventKind: "news",
    category: trigger.category ?? AutotradeTriggerCategory.LISTING,
    priceAlertType: TriggerPriceAlertType.SPIKE,
    priceChangeThreshold: "5",
    priceTimeframe: "5m",
    tradingPair: "",
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

interface AutotradeTriggerFormProps {
  userId: string;
  /** When provided the form is in edit mode; otherwise create mode. */
  trigger?: AutotradeTrigger;
  /** Other triggers for this user — used to hide already-assigned exchanges. */
  existingTriggers?: AutotradeTrigger[];
  onSaved: () => void;
}

const categoryOptions = [
  { value: AutotradeTriggerCategory.LISTING, label: "Lists" },
  { value: AutotradeTriggerCategory.DELISTING, label: "Delists" },
];

const eventKindOptions: {
  value: TriggerEventKind;
  label: string;
}[] = [
  { value: "news", label: "Listing / delisting" },
  { value: "price", label: "Price spike / drop" },
];

const priceMovementOptions = [
  { value: TriggerPriceAlertType.SPIKE, label: "Spikes" },
  { value: TriggerPriceAlertType.DROP, label: "Drops" },
];

export const AutotradeTriggerForm: React.FC<AutotradeTriggerFormProps> = ({
  userId,
  trigger,
  existingTriggers = [],
  onSaved,
}) => {
  const isEditing = Boolean(trigger);

  const [form, setForm] = useState<TriggerFormState>(
    trigger ? formStateFromTrigger(trigger) : defaultFormState
  );

  const [selectedExchanges, setSelectedExchanges] = useState<
    TriggerExchangeInput[]
  >(
    (trigger?.exchanges ?? []).map(({ exchangeCode, exchangeType }) => ({
      exchangeCode,
      exchangeType,
    }))
  );

  const { data: userExchanges } = useUserExchanges(userId);
  const connectedVenues = useMemo(
    () =>
      // Every listed account is live (connected and not deleted).
      (userExchanges ?? [])
        .slice()
        .sort(
          (a, b) =>
            (a.priority ?? Number.MAX_SAFE_INTEGER) -
            (b.priority ?? Number.MAX_SAFE_INTEGER)
        ),
    [userExchanges]
  );

  const createTriggerMutation = useCreateTrigger();
  const updateTriggerMutation = useUpdateTrigger();
  const deleteTriggerMutation = useDeleteTrigger();

  const updateForm = <K extends keyof TriggerFormState>(
    key: K,
    value: TriggerFormState[K]
  ) => setForm((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    if (form.eventKind !== "price") return;
    setSelectedExchanges((prev) => {
      const filtered = prev.filter((e) =>
        PRICE_AUTOTRADE_VENUES.some(
          (v) =>
            v.exchangeCode === e.exchangeCode &&
            v.exchangeType === e.exchangeType
        )
      );
      if (filtered.length > 0) return filtered;
      return [{ exchangeCode: "binance", exchangeType: "futures" }];
    });
  }, [form.eventKind]);

  const selectedKeys = useMemo(
    () =>
      new Set(
        selectedExchanges.map((e) => `${e.exchangeCode}-${e.exchangeType}`)
      ),
    [selectedExchanges]
  );

  const usedExchangeKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const existing of existingTriggers) {
      if (trigger && existing.id === trigger.id) continue;
      for (const exchange of existing.exchanges) {
        keys.add(`${exchange.exchangeCode}-${exchange.exchangeType}`);
      }
    }
    return keys;
  }, [existingTriggers, trigger]);

  const exchangeOptions = useMemo(() => {
    const options =
      form.eventKind === "price"
        ? PRICE_AUTOTRADE_VENUES.map((v) => ({
            key: v.venueId,
            exchangeCode: v.exchangeCode,
            exchangeType: v.exchangeType,
            label: v.label,
          }))
        : ALL_EXCHANGE_OPTIONS.map((o) => ({
            key: `${o.exchangeCode}-${o.exchangeType}`,
            exchangeCode: o.exchangeCode,
            exchangeType: o.exchangeType,
            label: o.label,
          }));

    return options.filter(
      (option) =>
        !usedExchangeKeys.has(
          `${option.exchangeCode}-${option.exchangeType}`
        )
    );
  }, [form.eventKind, usedExchangeKeys]);

  useEffect(() => {
    setSelectedExchanges((prev) =>
      prev.filter(
        (e) =>
          !usedExchangeKeys.has(`${e.exchangeCode}-${e.exchangeType}`)
      )
    );
  }, [usedExchangeKeys]);

  const toggleExchange = (exchangeCode: string, exchangeType: string) => {
    setSelectedExchanges((prev) => {
      const isSelected = prev.some(
        (e) =>
          e.exchangeCode === exchangeCode && e.exchangeType === exchangeType
      );
      if (isSelected) {
        return prev.filter(
          (e) =>
            !(
              e.exchangeCode === exchangeCode && e.exchangeType === exchangeType
            )
        );
      }
      return [...prev, { exchangeCode, exchangeType }];
    });
  };

  const thresholdNum = Number(form.priceChangeThreshold);
  const priceTf = (form.priceTimeframe ?? "").trim();
  const pricePair = (form.tradingPair ?? "").trim();
  const priceFieldsOk =
    form.eventKind === "news" ||
    (thresholdNum >= 0.1 && !Number.isNaN(thresholdNum) && priceTf.length > 0);

  const isValid =
    selectedExchanges.length > 0 &&
    form.amount > 0 &&
    form.leverage >= 1 &&
    !Number.isNaN(form.amount) &&
    !Number.isNaN(form.leverage) &&
    priceFieldsOk;

  const handleSave = async () => {
    if (!isValid) return;

    const tradingParams = {
      positionType: form.positionType,
      amount: Math.max(1, form.amount),
      leverage: Math.min(999, Math.max(1, Math.round(form.leverage))),
      exactLeverage: form.exactLeverage,
      takeProfitPercent: form.takeProfitPercent
        ? Number(form.takeProfitPercent)
        : undefined,
      stopLossPercent: form.stopLossPercent
        ? Number(form.stopLossPercent)
        : undefined,
      slippagePercent: form.slippagePercent
        ? Number(form.slippagePercent)
        : undefined,
      positionTimeout: form.positionTimeout
        ? Number(form.positionTimeout)
        : undefined,
    };

    try {
      if (isEditing && trigger) {
        if (trigger.triggerSource === TriggerSource.PRICE_ALERT) {
          const payload: UpdateTriggerPayload = {
            ...tradingParams,
            exchanges: selectedExchanges,
            priceAlertType: form.priceAlertType,
            priceChangeThreshold: thresholdNum,
            priceTimeframe: priceTf,
            tradingPair: pricePair || undefined,
          };
          await updateTriggerMutation.mutateAsync({
            triggerId: trigger.id,
            userId,
            payload,
          });
        } else {
          const payload: UpdateTriggerPayload = {
            ...tradingParams,
            exchanges: selectedExchanges,
            category: form.category,
            openOnAllExchanges: form.openOnAllExchanges,
          };
          await updateTriggerMutation.mutateAsync({
            triggerId: trigger.id,
            userId,
            payload,
          });
        }
        toast.success("Trigger updated");
      } else if (form.eventKind === "price") {
        const payload: CreatePriceAlertTriggerPayload = {
          triggerSource: TriggerSource.PRICE_ALERT,
          priceAlertType: form.priceAlertType,
          priceChangeThreshold: thresholdNum,
          priceTimeframe: priceTf,
          tradingPair: pricePair || undefined,
          exchanges: selectedExchanges,
          ...tradingParams,
        };
        await createTriggerMutation.mutateAsync({ userId, payload });
        toast.success("Trigger created");
      } else {
        const payload: CreateNewsAlertTriggerPayload = {
          triggerSource: TriggerSource.NEWS_ALERT,
          category: form.category,
          exchanges: selectedExchanges,
          openOnAllExchanges: form.openOnAllExchanges,
          ...tradingParams,
        };
        await createTriggerMutation.mutateAsync({ userId, payload });
        toast.success("Trigger created");
      }
      onSaved();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to save trigger";
      toast.error(message);
    }
  };

  const handleDelete = async () => {
    if (!trigger) return;
    try {
      await deleteTriggerMutation.mutateAsync({
        triggerId: trigger.id,
        userId,
      });
      toast.success("Trigger removed");
      onSaved();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to delete trigger";
      toast.error(message);
    }
  };

  const isSaving =
    createTriggerMutation.isPending || updateTriggerMutation.isPending;
  const isDeleting = deleteTriggerMutation.isPending;

  return (
    <div className="space-y-5">
      {!isEditing && (
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-text-secondary">
            Trigger type
          </p>
          <SegmentedControl<TriggerEventKind>
            options={eventKindOptions}
            value={form.eventKind}
            onChange={(v) => updateForm("eventKind", v)}
          />
        </div>
      )}

      {isEditing && trigger && (
        <p className="text-xs text-text-secondary">
          {trigger.triggerSource === TriggerSource.PRICE_ALERT
            ? "Price spike / drop trigger"
            : "Listing / delisting trigger"}
        </p>
      )}

      {form.eventKind === "news" && (
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-text-secondary">
            When a token
          </p>
          <SegmentedControl<AutotradeTriggerCategory>
            options={categoryOptions}
            value={form.category}
            onChange={(v) => updateForm("category", v)}
          />
        </div>
      )}

      {form.eventKind === "price" && (
        <>
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-wide text-text-secondary">
              When price
            </p>
            <SegmentedControl<TriggerPriceAlertType>
              options={priceMovementOptions}
              value={form.priceAlertType}
              onChange={(v) => updateForm("priceAlertType", v)}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-wide text-text-secondary">
                By at least (%)
              </p>
              <input
                type="number"
                min={0.1}
                step="0.1"
                className="w-full bg-primary/40 border border-gray-700 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-accent"
                value={form.priceChangeThreshold}
                onChange={(e) =>
                  updateForm("priceChangeThreshold", e.target.value)
                }
              />
            </div>
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-wide text-text-secondary">
                Within
              </p>
              <select
                className="w-full bg-primary/40 border border-gray-700 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-accent text-text-primary"
                value={form.priceTimeframe ?? "5m"}
                onChange={(e) => updateForm("priceTimeframe", e.target.value)}
              >
                {PRICE_TIMEFRAME_OPTIONS.map((tf) => (
                  <option key={tf} value={tf}>
                    {tf}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-wide text-text-secondary">
              Pair (optional)
            </p>
            <input
              type="text"
              className="w-full bg-primary/40 border border-gray-700 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-accent"
              placeholder="Leave empty for any symbol that fires the alert"
              value={form.tradingPair ?? ""}
              onChange={(e) => updateForm("tradingPair", e.target.value)}
            />
          </div>
        </>
      )}

      {/* Exchange selector — price alerts only support Binance & Gate futures */}
      <div className="space-y-2">
        <p className="text-xs uppercase tracking-wide text-text-secondary">
          {form.eventKind === "price"
            ? "Venue (price detector)"
            : "On these exchanges"}
        </p>

        <div className="flex flex-wrap gap-2">
          {exchangeOptions.map((option) => {
            const isSelected = selectedKeys.has(
              `${option.exchangeCode}-${option.exchangeType}`
            );
            return (
              <button
                key={option.key}
                type="button"
                onClick={() =>
                  toggleExchange(option.exchangeCode, option.exchangeType)
                }
                className={`px-3 py-1 rounded-full border text-xs font-medium transition-colors ${
                  isSelected
                    ? "border-accent/40 bg-accent/10 text-accent"
                    : "border-gray-700 bg-black/30 text-text-secondary hover:border-gray-500 hover:text-text-primary"
                }`}
              >
                {option.label}
                {isSelected ? " ✓" : ""}
              </button>
            );
          })}
        </div>
        {exchangeOptions.length === 0 && (
          <p className="text-xs text-text-secondary">
            All supported exchanges are already assigned to other triggers.
          </p>
        )}
        <p className="text-xs text-text-secondary">
          Trades open on your connected venues in the priority order set under
          Connected Exchanges. Drag that list to choose which exchange is tried
          first.
        </p>
      </div>

      {form.eventKind === "news" && (
        <ExecutionModePicker
          openOnAllExchanges={form.openOnAllExchanges}
          onChange={(v) => updateForm("openOnAllExchanges", v)}
          venueNames={connectedVenues.map((v) => v.name)}
          amount={form.amount}
          leverage={form.leverage}
        />
      )}

      {/* Direction */}
      <div className="space-y-2">
        <p className="text-xs uppercase tracking-wide text-text-secondary">
          Then do this
        </p>
        <TabSelector<"LONG" | "SHORT">
          options={[
            { value: "LONG", label: "Long" },
            { value: "SHORT", label: "Short" },
          ]}
          value={form.positionType}
          onChange={(v) => updateForm("positionType", v)}
          variant="compact"
        />
      </div>

      {/* Amount */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs uppercase tracking-wide text-text-secondary">
            Amount
          </p>
          <span className="text-xs text-text-secondary">
            {form.amount} USDT
          </span>
        </div>
        <input
          type="number"
          min={1}
          className="w-full bg-primary/40 border border-gray-700 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-accent"
          value={form.amount || ""}
          placeholder="0.00 USDT"
          onChange={(e) => updateForm("amount", Number(e.target.value) || 0)}
        />
        <input
          type="range"
          min={1}
          max={Math.max(500, form.amount || 500)}
          value={Math.min(form.amount || 1, Math.max(500, form.amount || 500))}
          onChange={(e) => updateForm("amount", Number(e.target.value))}
          className="w-full accent-accent"
        />
      </div>

      {/* Leverage */}
      <div className="space-y-2">
        <p className="text-xs uppercase tracking-wide text-text-secondary">
          Leverage
        </p>
        <input
          type="number"
          min={1}
          max={999}
          className="w-full bg-primary/40 border border-gray-700 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-accent"
          value={form.leverage || ""}
          onChange={(e) =>
            updateForm("leverage", Number(e.target.value) || form.leverage)
          }
        />
        <input
          type="range"
          min={1}
          max={Math.max(125, form.leverage || 125)}
          value={Math.min(
            form.leverage || 1,
            Math.max(125, form.leverage || 125)
          )}
          onChange={(e) => updateForm("leverage", Number(e.target.value))}
          className="w-full accent-accent"
        />
      </div>

      {/* TP / SL / Auto-close */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-text-secondary">
            Take Profit %
          </p>
          <PriceMoveRoiHint
            variant="tp"
            percentStr={form.takeProfitPercent}
            leverage={form.leverage}
            marginUsdt={form.amount}
          />
          <input
            type="number"
            min={0}
            max={1000}
            step="0.1"
            className="w-full bg-primary/40 border border-gray-700 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-accent"
            value={form.takeProfitPercent}
            onChange={(e) => updateForm("takeProfitPercent", e.target.value)}
            placeholder="Optional"
          />
        </div>
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-text-secondary">
            Stop Loss %
          </p>
          <PriceMoveRoiHint
            variant="sl"
            percentStr={form.stopLossPercent}
            leverage={form.leverage}
            marginUsdt={form.amount}
          />
          <input
            type="number"
            min={0}
            max={100}
            step="0.1"
            className="w-full bg-primary/40 border border-gray-700 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-accent"
            value={form.stopLossPercent}
            onChange={(e) => updateForm("stopLossPercent", e.target.value)}
            placeholder="Optional"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-text-secondary">
            Auto-close (m)
          </p>
          <input
            type="number"
            min={1}
            max={1440}
            className="w-full bg-primary/40 border border-gray-700 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-accent"
            value={form.positionTimeout}
            onChange={(e) => updateForm("positionTimeout", e.target.value)}
            placeholder="Optional"
          />
        </div>
      </div>

      {/* Rule preview */}
      {selectedExchanges.length > 0 && (
        <div className="bg-black/30 border border-gray-800 rounded-xl px-4 py-3">
          <p className="text-sm text-text-secondary leading-relaxed">
            <span className="text-text-primary font-medium">Preview: </span>
            {form.eventKind === "news" ? (
              <>
                When a token{" "}
                <span className="text-[#4F6EFF] font-medium">
                  {form.category === AutotradeTriggerCategory.LISTING
                    ? "lists"
                    : "delists"}{" "}
                </span>
                on{" "}
                <span className="text-accent font-medium">
                  {selectedExchanges
                    .map(
                      (e) =>
                        `${formatExchangeName(e.exchangeCode)} - ${formatTypeLabel(e.exchangeType)}`
                    )
                    .join(", ")}
                </span>
                , go{" "}
                <span
                  className={
                    "font-medium" +
                    (form.positionType === "LONG"
                      ? " text-green-500"
                      : " text-red-500")
                  }
                >
                  {form.positionType === "LONG" ? "long" : "short"}
                </span>{" "}
                with {form.amount} USDT, leverage {form.leverage}x
                {form.takeProfitPercent
                  ? `, TP ${form.takeProfitPercent}%`
                  : ""}
                {form.stopLossPercent ? `, SL ${form.stopLossPercent}%` : ""}
                {form.openOnAllExchanges ? (
                  <>
                    {" "}
                    on{" "}
                    <span className="text-amber-400 font-medium">
                      every connected exchange at once
                    </span>
                  </>
                ) : null}
                .
              </>
            ) : (
              <>
                When price{" "}
                <span className="text-[#4F6EFF] font-medium">
                  {form.priceAlertType === TriggerPriceAlertType.SPIKE
                    ? "spikes"
                    : "drops"}{" "}
                </span>
                by at least {form.priceChangeThreshold}% within{" "}
                {form.priceTimeframe ?? "—"}
                {pricePair ? ` on pair ${pricePair.toUpperCase()}` : ""} on{" "}
                <span className="text-accent font-medium">
                  {selectedExchanges
                    .map(
                      (e) =>
                        `${formatExchangeName(e.exchangeCode)} - ${formatTypeLabel(e.exchangeType)}`
                    )
                    .join(", ")}
                </span>
                , go{" "}
                <span
                  className={
                    "font-medium" +
                    (form.positionType === "LONG"
                      ? " text-green-500"
                      : " text-red-500")
                  }
                >
                  {form.positionType === "LONG" ? "long" : "short"}
                </span>{" "}
                with {form.amount} USDT, leverage {form.leverage}x
                {form.takeProfitPercent
                  ? `, TP ${form.takeProfitPercent}%`
                  : ""}
                {form.stopLossPercent ? `, SL ${form.stopLossPercent}%` : ""}.
              </>
            )}
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3 pt-1">
        {isEditing && (
          <button
            type="button"
            disabled={isDeleting}
            onClick={handleDelete}
            className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-red-400 text-sm font-semibold border border-red-500/30 hover:bg-red-500/10 disabled:opacity-60 transition-colors"
          >
            {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Delete
          </button>
        )}
        <button
          type="button"
          disabled={!isValid || isSaving}
          onClick={handleSave}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-accent text-white text-sm font-semibold hover:bg-accent/80 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
          {isEditing ? "Save changes" : "Create trigger"}
        </button>
      </div>
    </div>
  );
};

// ─── Execution mode ───────────────────────────────────────────────────────────

interface ExecutionModePickerProps {
  openOnAllExchanges: boolean;
  onChange: (openOnAllExchanges: boolean) => void;
  /** Connected, active exchanges in priority order. */
  venueNames: string[];
  amount: number;
  leverage: number;
}

const ExecutionModePicker: React.FC<ExecutionModePickerProps> = ({
  openOnAllExchanges,
  onChange,
  venueNames,
  amount,
  leverage,
}) => {
  const venueCount = venueNames.length;
  const maxMargin = amount * Math.max(venueCount, 1);

  const options = [
    {
      value: false,
      testId: "execution-mode-sequential",
      icon: <ListOrdered className="h-4 w-4" />,
      title: "One at a time",
      body: "Tries your exchanges in priority order and stops at the first fill. Never more than one position.",
    },
    {
      value: true,
      testId: "execution-mode-all",
      icon: <Zap className="h-4 w-4" />,
      title: "All at once",
      body: "Opens on all your exchanges at once. No waiting for a failed order to fall back, and more size in the first seconds.",
    },
  ];

  return (
    <div className="space-y-2">
      <p className="text-xs uppercase tracking-wide text-text-secondary">
        Execution
      </p>
      <div
        role="radiogroup"
        aria-label="Execution"
        className="grid grid-cols-1 sm:grid-cols-2 gap-2"
      >
        {options.map((option) => {
          const isSelected = option.value === openOnAllExchanges;
          const isPower = option.value;
          return (
            <button
              key={option.testId}
              type="button"
              role="radio"
              aria-checked={isSelected}
              data-testid={option.testId}
              onClick={() => onChange(option.value)}
              className={`relative text-left rounded-xl border px-3 py-2.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 ${
                isSelected
                  ? isPower
                    ? "border-amber-400/60 bg-gradient-to-br from-amber-400/15 to-orange-500/5"
                    : "border-accent/50 bg-accent/10"
                  : "border-gray-700 bg-black/30 hover:border-gray-500"
              }`}
            >
              <div className="flex items-center gap-2">
                <span
                  className={`flex h-6 w-6 items-center justify-center rounded-lg ${
                    isSelected
                      ? isPower
                        ? "bg-amber-400/20 text-amber-300"
                        : "bg-accent/20 text-accent"
                      : "bg-white/5 text-text-secondary"
                  }`}
                >
                  {option.icon}
                </span>
                <span
                  className={`text-sm font-semibold ${
                    isSelected ? "text-text-primary" : "text-text-secondary"
                  }`}
                >
                  {option.title}
                </span>
                {isPower && (
                  <span className="ml-auto rounded-full bg-amber-400/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-300">
                    Power
                  </span>
                )}
              </div>
              <p className="mt-1.5 text-xs leading-relaxed text-text-secondary">
                {option.body}
              </p>
            </button>
          );
        })}
      </div>

      {openOnAllExchanges && (
        <div
          data-testid="execution-mode-all-warning"
          className="flex gap-2.5 rounded-xl border border-amber-400/30 bg-amber-400/5 px-3 py-2.5"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-300" />
          <div className="space-y-1.5 text-xs leading-relaxed text-text-secondary">
            {venueCount > 1 ? (
              <p>
                Up to{" "}
                <span className="font-semibold text-text-primary">
                  {venueCount} positions
                </span>{" "}
                with {amount || 0} USDT each, so up to{" "}
                <span className="font-semibold text-text-primary">
                  {maxMargin} USDT margin
                </span>{" "}
                ({maxMargin * (leverage || 1)} USDT at {leverage || 1}x). Only
                exchanges that list the token open a position.
              </p>
            ) : (
              <p>
                You have {venueCount === 1 ? "one" : "no"} connected exchange,
                so this works the same as one at a time. Connect more
                exchanges to open on several at once.
              </p>
            )}
            {venueCount > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {venueNames.map((name) => (
                  <span
                    key={name}
                    className="rounded-full border border-amber-400/25 bg-black/30 px-2 py-0.5 text-[11px] font-medium text-amber-200"
                  >
                    {name}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
